import {
  jam,
  getJamClient,
  createUuid,
  jamUpdate,
  createEmail as createEmailFunc,
  requestUpdatedThread,
} from 'libs/jamUtil'

import { getEnvParam, getAppServices } from 'env'
import { useScheduledEmail } from 'vars'

import { infoDB } from 'myStore'
import i18n from 'i18n'
import localforage from 'myStore'

import {
  updateToken,
  updateTokenIfExpired,
  addUpdateTokenListener,
  removeUpdateTokenListener,
} from 'services/jwt'
import { checkOnline } from 'utils'

import allowedSenderDB from 'libs/DB/allowedSenderDB'
import tempEmailDB from 'libs/DB/tempEmailDB'
import { outboxDB, attachmentDB } from 'libs/DB/outboxDB'
import orgChartDB from 'libs/DB/orgChartDB'
import sharedContactDB from 'libs/DB/sharedContactDB'
import emailDB from 'libs/DB/emailDB'
import emailDetailDB from 'libs/DB/emailDetailDB'

import knownSenderCache from 'libs/CACHE/knownSenderCache'
import jmapDownloadCache from 'libs/CACHE/jmapDownloadCache'

import settings from 'popups/Settings'
import { isPopupMode } from 'services/popup/isPopupMode'
import settingSyncService from 'services/settingSync'
import {
  addFavoriteEntry,
  buildFavoriteEntryMaps,
  convertLegacyIdsToEntries,
  normalizeFavoriteEntries,
  removeFavoriteEntry,
  renameFavoriteEntries,
  resolveFavoriteEntries,
  resolveFavoriteEntryToId,
  toVirtualFavoriteEntry,
} from 'redux/settings/favoriteMailboxes'
import type { FavoriteEntryMaps } from 'redux/settings/favoriteMailboxes'
import { toImapPath } from 'libs/jamUtil/openSearchMap'

import attachmentHelper from 'pages/main/components/Attachment/helper'
import orgChartHelper from 'pages/contacts/components/OrgChart/Helper'
import contactHelper from 'pages/contacts/components/SharedContacts/Helper'

import { isEmail, stringToHash, creactReactObjMgr } from 'utils/ui'
import { UIQueue } from 'utils/ui/UIQueue'

import ThreadObj from './ThreadObj'
import { handleJmapCallError } from './jmapCallError'
import { JMAPEmail } from './JMAPMailbox'
import {
  JMAPMailbox,
  FilterMailbox,
  UnreadMailbox,
  SearchMailbox,
  createAiSummaryForNew,
  clearJmapMailboxCaches,
} from './JMAPMailbox'
import { ScheduleMailbox } from './ScheduleMailbox'
import { SpamSniperMailbox } from './SpamSniperMailbox'

import { EMAIL_STATE, TYPE, ACTION, ListenerObj, applyData, extendJmapState } from './defConstants'
import { getAvatarURL, clearAvatarCache } from './avatarService'
import { AddressObj } from './AddressObjClass'

const useOutbox = false

/**
 * mailbox, thread, email은 data와 obj로 구분,
 * 각 obj는 내부에 data 객체를 통해 원본 데이터를 관리.
 */

// Type definitions
interface UserInfo {
  email: string
  [key: string]: any
}

interface MailboxContainer {
  allList: any[]
  list: any[]
  map: { [key: string]: any }
  parentMap: { [key: string]: any }
  trash: any
  drafts?: any
  changeListener: any
}

interface IdentityContainer {
  list: any[]
  map: { [key: string]: any }
  default: any
  changeListener: any
}

interface EmailData {
  id: string
  messageId?: string
  from?: any[]
  to?: any[]
  cc?: any[]
  bcc?: any[]
  subject?: string
  replyTo?: any[]
  receivedAt?: string
  keywords?: { [key: string]: boolean }
  preview?: string
  mailboxIds: { [key: string]: boolean }
  [key: string]: any
}

interface ThreadData {
  id: string
  emailIds: string[]
  [key: string]: any
}

interface EmailInfo {
  email: string
  name?: string
  [key: string]: any
}

interface NotiInfo {
  [key: string]: any
}

interface Event0 {
  [type: string]: {
    [action: string]: {
      [targetType: string]: {
        [id: string]: any[]
        all?: any[]
      }
    }
  }
}

interface EventObj {
  type: number
  action: number
  targetType: number
  target: any
  data: { list: any[] }
}

interface OutboxData {
  enabled: boolean
  list: any[]
  map: { [key: string]: any }
  draft: {
    list: string[]
    map: { [key: string]: boolean }
  }
  changeListener: any
  applyOutboxData: (email: any, emailData: any) => void
  load: () => Promise<void>
  add: (emailData: any, attachments?: any[], draftId?: string) => Promise<any>
  remove: (emailId: string) => Promise<any>
  getList: () => any[]
}

interface MailData {
  draftsBox?: any
  sentBox?: any
  identityId?: string
  htmlText?: string
  [key: string]: any
}

interface InfoDBKey {
  recentEmailList?: string
  recentMailboxList?: string
  favoriteMailboxList?: string
  mailboxExpandedKeys?: string
  [key: string]: string | undefined
}

interface MailboxExpandedKeyData {
  isInitialized: boolean
  idList: string[]
  version: number
}

interface SignatureAPI {
  getList: () => any[]
  close: () => void
}

type EventSourceType = EventSource & {
  addEventListener: (type: string, listener: (event: any) => void) => void
  removeEventListener: (type: string, listener: (event: any) => void) => void
  close: () => void
}

function DataManager(this: any) {
  const self = this

  self.reactMgr = creactReactObjMgr()

  /* mailbox item의 구조
    {
      data: { ... },  // 실제 mailbox의 데이터.
      list: [...] // child folder의 list.
    }
  */

  // self._isDefaultMailboxChecked = false;

  self.maxChanges = 20

  self.curMailbox = null

  /** @type {JMAPMailboxContainer | undefined} */
  self.JMAPMailboxContainer = undefined

  self.mailbox = {
    allList: [],
    list: [],
    map: {},
    parentMap: {},
    trash: null,
    changeListener: new ListenerObj('MailboxChange', self),
  } as MailboxContainer

  self.identity = {
    list: [],
    map: {},
    default: null,
    changeListener: new ListenerObj('IdentityChange', self),
  } as IdentityContainer

  self.emailChangeListener = new ListenerObj('EmailChange', self)
  self.threadChangeListener = new ListenerObj('ThreadChange', self)

  // 발송이 진행 중인 draft id 집합. 발송 시작 시 SendHelper가 등록하고 실패 시 해제한다.
  // viewer [초안] 배지 결정(emailHelper.shouldShowDraftBadge)에서 사용 — 발송 중인 메일이
  // 지연된 setThread로 [초안]으로 그려지는 것을 차단한다.
  self.sendingEmailIds = new Set<string>()

  self.orgChartData = {} as any

  self.normalMailboxMap = {} as { [key: string]: any }

  self.filteredMailboxMap = {} as { [key: string]: any }

  self.unreadCount = 0

  extendJmapState(self)

  self.mailboxReq = {} as any

  extendJmapState(self.mailboxReq)

  // known sender 캐시 coordination 상태.
  // - knownSenderInFlight: 동일 email 동시 조회 dedup용 Map
  // - knownSenderGeneration: clear() 중간 발생 시 stale write 차단용 세대 카운터
  const knownSenderInFlight = new Map<string, Promise<boolean>>()
  let knownSenderGeneration = 0

  const clearKnownSenderCache = (): void => {
    // BaseCache.clear()는 dataMap 비우기를 동기적으로 수행하고 Promise.resolve(true)를 반환하므로
    // await 없이도 호출 직후 모든 읽기가 빈 상태를 관측한다.
    // 향후 knownSenderCache를 BaseDB 기반(IDB 트랜잭션)으로 교체한다면 이 함수를 async로 바꿔야 한다.
    knownSenderCache.clear()
    knownSenderInFlight.clear()
    knownSenderGeneration += 1
  }

  self.init = () => {
    self.closeEvtSrc()

    // firstLoad에서 등록한 visibilitychange 리스너 해제. firstLoad 재호출 시 재등록된다.
    if (self.onVisibilityChange != null) {
      document.removeEventListener('visibilitychange', self.onVisibilityChange)
    }

    self.isFirstLoadCalled = false

    self.allEmailMap = {} as { [key: string]: any }
    self.allThreadMap = {} as { [key: string]: any }

    // 발송 중 draft id 집합도 계정 전환/재로그인 시 리셋(세션 누적 방지).
    self.sendingEmailIds.clear()

    // 주소 객체 캐시는 email+name 조합마다 무한 누적되므로 계정 전환/재로그인 시 리셋한다.
    // UI가 보유한 기존 AddressObj는 자기완결로 동작하다가 리렌더 시 새 객체로 대체된다.
    self.addressMap = {} as { [key: string]: AddressObj }

    self.orgChartData = {}

    // 세션 종속 캐시 리셋: cache/inflight Map/generation 카운터 모두 초기화.
    // 로그인(landing → init) 및 로그아웃(clearAll → init) 양쪽에서 호출됨.
    clearKnownSenderCache()
    // blobId는 account 스코프이므로 계정 전환/재로그인 경로에서 stale 항목이 남지 않도록 init에서도 비운다.
    jmapDownloadCache.clear()
    // JMAPMailbox 모듈 캐시(aiSummary Map 2종 + 요청 큐) 및 아바타 생성기 캐시 정리.
    clearJmapMailboxCaches()
    clearAvatarCache()

    // DataManager의 lastState는 emailList에 대한 처리에만 사용.
    // mailbox에는 사용되지 않음.
    self.setLastState(undefined)
    self.setLastQueryState(undefined)

    self.mailboxReq.setLastState(undefined)
    self.mailboxReq.setLastQueryState(undefined)

    self.curMailbox = null

    self.mailbox = {
      allList: [],
      list: [],
      map: {},
      parentMap: {},
      trash: null,
      changeListener: new ListenerObj('MailboxChange', self),
    }

    self.identity = {
      list: [],
      map: {},
      default: null,
      changeListener: new ListenerObj('IdentityChange', self),
    }

    self.emailChangeListener = new ListenerObj('EmailChange', self)
    self.threadChangeListener = new ListenerObj('ThreadChange', self)

    self.unreadCount = 0

    self.searchMailbox = new SearchMailbox(self, {})

    self.normalMailboxMap = {}

    if (useScheduledEmail === true) {
      self.normalMailboxMap.schedule = new ScheduleMailbox(self, {
        id: 'schedule',
        role: 'schedule',
        name: 'schedule',
      })
    }

    self.appServices = getAppServices() || {}
    if (self.appServices.spam_sniper === true) {
      self.normalMailboxMap.spamsniper = new SpamSniperMailbox(self, {
        id: 'spamsniper',
        role: 'spamsniper',
        name: 'spamsniper',
      })
    }

    // filteredMailboxMap은 id와 role을 동일하게 key로 사용하므로, id와 role이 같아야 함.
    self.filteredMailboxMap = {
      unread: new UnreadMailbox(self, {
        // listType: TYPE.THREAD,
        id: 'unread',
        role: 'unread',
        name: 'unread',
        getJmapFilter: () => {
          // canCalculateChanges 값에 따라 사용여부 결정됨.
          const inMailboxOtherThan: string[] = []
          const archiveMailbox = self.getMailboxForRole('archive')
          if (archiveMailbox != null) {
            inMailboxOtherThan.push(archiveMailbox.getId())
          }
          const junkMailbox = self.getMailboxForRole('junk')
          if (junkMailbox != null) {
            inMailboxOtherThan.push(junkMailbox.getId())
          }
          const sentMailbox = self.getMailboxForRole('sent')
          if (sentMailbox != null) {
            inMailboxOtherThan.push(sentMailbox.getId())
          }
          const trashMailbox = self.getMailboxForRole('trash')
          if (trashMailbox != null) {
            inMailboxOtherThan.push(trashMailbox.getId())
          }
          // outbox가 있는 경우에는 outbox도 제외.
          const draftMailbox = self.getMailboxForRole('drafts')
          if (draftMailbox != null) {
            inMailboxOtherThan.push(draftMailbox.getId())
          }

          return {
            // conditions: [
            //   {
            //     inMailboxOtherThan,
            //   },
            //   {
            //     notKeyword: '$seen',
            //   },
            // ],
            // operator: 'AND',
            inMailboxOtherThan,
            notKeyword: '$seen',
          }
        },
        getResultFilter: () => {
          const mailboxIds: string[] = []
          const archiveMailbox = self.getMailboxForRole('archive')
          if (archiveMailbox != null) {
            mailboxIds.push(archiveMailbox.getId())
          }
          const junkMailbox = self.getMailboxForRole('junk')
          if (junkMailbox != null) {
            mailboxIds.push(junkMailbox.getId())
          }
          const sentMailbox = self.getMailboxForRole('sent')
          if (sentMailbox != null) {
            mailboxIds.push(sentMailbox.getId())
          }
          const trashMailbox = self.getMailboxForRole('trash')
          if (trashMailbox != null) {
            mailboxIds.push(trashMailbox.getId())
          }
          // outbox가 있는 경우에는 outbox도 제외.
          const draftMailbox = self.getMailboxForRole('drafts')
          if (draftMailbox != null) {
            mailboxIds.push(draftMailbox.getId())
          }
          const filterFunc = (emailData: EmailData) => {
            const isInvalidMailbox = mailboxIds.some((id) => emailData.mailboxIds[id] === true)
            return isInvalidMailbox !== true && emailData.keywords?.$seen !== true
          }
          return filterFunc
        },
      }),
      flagged: new FilterMailbox(self, {
        // listType: TYPE.THREAD,
        id: 'flagged',
        role: 'flagged',
        name: 'flagged',
        useUnreadCnt: false,
        getJmapFilter: () => {
          const inMailboxOtherThan: string[] = []
          const archiveMailbox = self.getMailboxForRole('archive')
          if (archiveMailbox != null) {
            inMailboxOtherThan.push(archiveMailbox.getId())
          }
          const junkMailbox = self.getMailboxForRole('junk')
          if (junkMailbox != null) {
            inMailboxOtherThan.push(junkMailbox.getId())
          }
          const sentMailbox = self.getMailboxForRole('sent')
          if (sentMailbox != null) {
            inMailboxOtherThan.push(sentMailbox.getId())
          }
          const trashMailbox = self.getMailboxForRole('trash')
          if (trashMailbox != null) {
            inMailboxOtherThan.push(trashMailbox.getId())
          }
          // outbox가 있는 경우에는 outbox도 제외.
          const draftMailbox = self.getMailboxForRole('drafts')
          if (draftMailbox != null) {
            inMailboxOtherThan.push(draftMailbox.getId())
          }
          return {
            // conditions: [
            //   {
            //     inMailboxOtherThan,
            //   },
            //   {
            //     hasKeyword: '$flagged',
            //   },
            // ],
            // operator: 'AND',
            inMailboxOtherThan,
            hasKeyword: '$flagged',
          }
        },
        getResultFilter: () => {
          const mailboxIds: string[] = []
          const archiveMailbox = self.getMailboxForRole('archive')
          if (archiveMailbox != null) {
            mailboxIds.push(archiveMailbox.getId())
          }
          const junkMailbox = self.getMailboxForRole('junk')
          if (junkMailbox != null) {
            mailboxIds.push(junkMailbox.getId())
          }
          const sentMailbox = self.getMailboxForRole('sent')
          if (sentMailbox != null) {
            mailboxIds.push(sentMailbox.getId())
          }
          const trashMailbox = self.getMailboxForRole('trash')
          if (trashMailbox != null) {
            mailboxIds.push(trashMailbox.getId())
          }
          // outbox가 있는 경우에는 outbox도 제외.
          const draftMailbox = self.getMailboxForRole('drafts')
          if (draftMailbox != null) {
            mailboxIds.push(draftMailbox.getId())
          }
          const filterFunc = (emailData: EmailData) => {
            const isInvalidMailbox = mailboxIds.some((id) => emailData.mailboxIds[id] === true)
            return isInvalidMailbox !== true && emailData.keywords?.$flagged === true
          }
          return filterFunc
        },
      }),
    }

    addUpdateTokenListener(self.onUpdateToken)
  }

  self.updateNormalMailbox = async (): Promise<any[]> => {
    const keys = Object.keys(self.normalMailboxMap)
    const promises = keys.map(async (key) => {
      const mailbox = self.normalMailboxMap[key]
      return await mailbox.updateData()
    })
    return await Promise.all(promises)
  }

  self.updateFilteredMailbox = async (): Promise<any[]> => {
    const keys = Object.keys(self.filteredMailboxMap)
    const promises = keys.map(async (key) => {
      const mailbox = self.filteredMailboxMap[key]
      return await mailbox.updateData()
    })
    return await Promise.all(promises)
  }

  self.setUserInfo = (userInfo: UserInfo): void => {
    self.userInfo = userInfo
    self.createInfoDBKeys(userInfo.email)
  }

  self.getUserInfo = (): UserInfo => {
    return self.userInfo
  }

  self.clearAll = (): void => {
    self.init()
  }

  // origin = string, type = int, action= int, target = { type, id }, data = object
  const applyEvent0 = (
    event0: Event0,
    type: number,
    action: number,
    target: { type: number; id: string },
    func?: (dataList: any[]) => any,
  ): any => {
    event0[type] = event0[type] || {}
    const typeProps = event0[type]
    typeProps[action] = typeProps[action] || {}
    const actionProps = typeProps[action]
    actionProps[target.type] = actionProps[target.type] || {}
    const targetType = actionProps[target.type]
    targetType[target.id] = targetType[target.id] || []
    const dataList = targetType[target.id]
    if (func != null) {
      return func(dataList)
    }
    return null
  }

  self.isExistEvent0Obj = (
    event0: Event0,
    type: number,
    action: number,
    target: { type: number; id: string },
    dataObj: any,
  ): boolean => {
    return applyEvent0(event0, type, action, target, (dataList) => {
      const pos = dataList.findIndex((item) => item.getId() === dataObj.getId())
      return pos > -1
    })
  }

  self.addEvent0Obj = (
    event0: Event0,
    type: number,
    action: number,
    target: { type: number; id: string },
    dataObj: any,
  ): void => {
    applyEvent0(event0, type, action, target, (dataList) => {
      const pos = dataList.findIndex((item) => item.getId() === dataObj.getId())
      if (pos < 0) {
        dataList.push(dataObj)
      }
    })
  }

  self.removeEvent0Obj = (
    event0: Event0,
    type: number,
    action: number,
    target: { type: number; id: string },
    dataObj: any,
  ): void => {
    applyEvent0(event0, type, action, target, (dataList) => {
      const pos = dataList.findIndex((item) => item.getId() === dataObj.getId())
      if (pos > -1) {
        dataList.splice(pos, 1)
      }
    })
  }

  const getTarget = (targetType: number, targetId: string | null): any => {
    let target = null
    switch (targetType) {
      case TYPE.EMAIL:
        target = self.getEmailById(targetId)
        break
      case TYPE.EMAIL:
        target = self.getThreadById(targetId)
        break
      case TYPE.MAILBOX:
        target = self.getMailboxById(targetId)
        break
      case TYPE.DATAMANAGER:
        target = self
        break
      default:
        target = null
        break
    }

    return target
  }

  self.makeEvent0 = (event0: Event0): EventObj[] => {
    const eventList: EventObj[] = []
    Object.entries(event0).forEach(([type, typeValue]) => {
      Object.entries(typeValue).forEach(([action, actionValue]) => {
        Object.entries(actionValue).forEach(([targetType, targetValue]) => {
          if (targetValue.all == null) {
            Object.entries(targetValue).forEach(([key, list]) => {
              if ((list as any[]).length > 0) {
                const targetTypeValue = parseInt(targetType)
                eventList.push({
                  type: parseInt(type),
                  action: parseInt(action),
                  targetType: targetTypeValue,
                  target: getTarget(targetTypeValue, key),
                  data: { list: list as any[] },
                })
              }
            })
          } else {
            if (actionValue[TYPE.DATAMANAGER].all.length > 0) {
              const targetTypeValue = TYPE.DATAMANAGER
              eventList.push({
                type: parseInt(type),
                action: parseInt(action),
                targetType: targetTypeValue,
                target: getTarget(targetTypeValue, null),
                data: { list: actionValue[targetTypeValue].all },
              })
            }
          }
        })
      })
    })

    return eventList
  }

  self.getEmailObj = (emailId: string): any => {
    if (self.allEmailMap[emailId] == null) {
      self.allEmailMap[emailId] = new JMAPEmail(self, emailId)
    }

    return self.allEmailMap[emailId]
  }

  // 서버에서 전달받은 데이터를 반영한다.
  self.applyEmailData = (
    emailData: EmailData,
    notiInfo?: NotiInfo,
    runSort?: boolean,
    mailboxInfos?: any[],
  ): any => {
    const emailObj = self.getEmailObj(emailData.id)
    emailObj.applyData(emailData, notiInfo, false, runSort, mailboxInfos)

    return emailObj
  }

  // emailId만 있는 경우, 정보를 얻어오기 위해 사용되는 함수임.
  self.applyEmailById = async (emailId: string, useNoti?: boolean): Promise<any> => {
    if (self.allEmailMap[emailId] == null) {
      self.allEmailMap[emailId] = new JMAPEmail(self, emailId)
    }

    await self.allEmailMap[emailId].loadSelf(useNoti)
    return self.allEmailMap[emailId]
  }

  // 서버에서 전달받은 데이터를 반영한다.
  self.applyThreadData = (
    threadData: ThreadData,
    notiInfo?: NotiInfo,
    runSort?: boolean,
    mailboxInfos?: any[],
  ): any => {
    if (self.allThreadMap[threadData.id] == null) {
      self.allThreadMap[threadData.id] = new ThreadObj(self, threadData.id)
    }
    const threadObj = self.allThreadMap[threadData.id]
    threadObj.applyData(threadData, notiInfo, false, runSort, mailboxInfos)

    return threadObj
  }

  self.noMailboxFilter = (/* emailData: EmailData */): boolean => {
    // const mailboxMap = emailData.mailboxIds;
    // if (Object.keys(mailboxMap).length === 0) { // mailboxId 정보가 없으면 표시하지 않음.
    //   return false;
    // }
    return true
  }

  self.setCurMailbox = (mailbox: any): void => {
    self.curMailbox = mailbox
  }

  // 전체(모든 mailbox)에서 변경된 내용을 검사하는 함수임. DataManager로 이동 필요.
  self.reqUpdatedAllThreadData = (): Promise<any> => {
    const lastState = self.getLastState()
    // const lastQueryState = self.getLastQueryState()

    const params: any = {
      maxChanges: self.maxChanges,
    }

    if (lastState != null) {
      params.sinceState = lastState
    }
    // if (lastQueryState != null) {
    //   params.sinceQueryState = lastQueryState
    // }

    return requestUpdatedThread(params)
      .then(({ emailChanges, createdEmails, updatedEmails, destroyedEmails, info }) => {
        console.log(
          ' -- requestUpdatedThread : ',
          emailChanges,
          createdEmails,
          updatedEmails,
          destroyedEmails,
          info,
        )

        if (info.lastState != null) {
          self.setLastState(info.lastState)
        }
        // if (info.lastQueryState) {
        //   self.setLastQueryState(info.lastQueryState)
        // }

        // @todo - 지워지거나, 변경된 부분을 여기서 업데이트하고, 그 결과를 notify해야 함.

        const notiInfo: NotiInfo = {}
        const mailboxMap: { [key: string]: any } = []

        // 추가로 변경된 데이터가 있는 경우 처리 필요.
        if (emailChanges.hasMoreChanges === true) {
          // 현재 폴더 초기화 - reload~!!
        }

        createdEmails.list.filter(self.noMailboxFilter).forEach((emailData: EmailData) => {
          Object.keys(emailData.mailboxIds).forEach((mailboxId) => {
            const mailbox = self.getMailboxById(mailboxId)
            if (mailbox != null) {
              const newEmailData = {
                ...emailData,
                extra: {
                  isFake: true,
                  mailboxObj: mailbox,
                },
              }
              mailbox.addEmailData(newEmailData, notiInfo, false)
              if (mailboxMap[mailboxId] == null) {
                mailboxMap[mailboxId] = mailbox
              }
            }
          })
        })

        // 현재 보고 있는 mailbox(curMailbox)가 thread-type(isEmailType === false)이면,
        // 변경 이메일이 직접 속한 mailbox 가 아니어도 같은 thread 의 다른 이메일을 통해
        // 이 thread 를 표시 중일 수 있다. 그 경우 curMailbox 도 갱신 대상이므로 동일 op 적용.
        // 기본 루프(emailData.mailboxIds)에서 이미 처리되는 경우는 제외(중복 방지).
        const getAffectedCurMailbox = (emailData: EmailData): any => {
          const curMailbox = self.curMailbox
          if (
            curMailbox == null ||
            curMailbox.isEmailType !== false ||
            emailData == null ||
            emailData.mailboxIds == null ||
            emailData.mailboxIds[curMailbox.getId()] === true
          ) {
            return null
          }
          const threadObj = self.getThreadById(emailData.threadId)
          if (threadObj == null) {
            return null
          }
          const isAffected = threadObj.data.emailIds.some((emailId: string) => {
            const emailObj = self.getEmailById(emailId)
            return emailObj != null && curMailbox.isContain(emailObj) === true
          })
          return isAffected === true ? curMailbox : null
        }

        updatedEmails.list.filter(self.noMailboxFilter).forEach((emailData: EmailData) => {
          const affectedCurMailbox = getAffectedCurMailbox(emailData)
          Object.keys(emailData.mailboxIds).forEach((mailboxId) => {
            const mailbox = self.getMailboxById(mailboxId)
            if (mailbox != null) {
              mailbox.addEmailData(emailData, notiInfo, false)
              if (mailboxMap[mailboxId] == null) {
                mailboxMap[mailboxId] = mailbox
              }
            }
          })
          if (affectedCurMailbox != null) {
            affectedCurMailbox.addEmailData(emailData, notiInfo, false)
            const curMailboxId = affectedCurMailbox.getId()
            if (mailboxMap[curMailboxId] == null) {
              mailboxMap[curMailboxId] = affectedCurMailbox
            }
          }
        })

        destroyedEmails.list.forEach((emailData: EmailData) => {
          const affectedCurMailbox = getAffectedCurMailbox(emailData)
          Object.keys(emailData.mailboxIds).forEach((mailboxId) => {
            const mailbox = self.getMailboxById(mailboxId)
            if (mailbox != null) {
              mailbox.removeEmailData(emailData, notiInfo, affectedCurMailbox == null)
              if (mailboxMap[mailboxId] == null) {
                mailboxMap[mailboxId] = mailbox
              }
            }
          })
          if (affectedCurMailbox != null) {
            // threadObj에서 제거되어야 함.
            affectedCurMailbox.removeEmailData(emailData, notiInfo, true)
            const curMailboxId = affectedCurMailbox.getId()
            if (mailboxMap[curMailboxId] == null) {
              mailboxMap[curMailboxId] = affectedCurMailbox
            }
          }
        })

        // 서버에서 지워진 것은 정보가 없으므로, notFound로 전달됨.
        // client에서는 아직 데이터가 존재하므로, 찾아서 지워야 함.
        destroyedEmails.notFound.forEach((emailId: string) => {
          const email = self.getEmailById(emailId)
          if (email != null && email.data != null) {
            const affectedCurMailbox = getAffectedCurMailbox(email.data)
            Object.keys(email.data.mailboxIds).forEach((mailboxId) => {
              const mailbox = self.getMailboxById(mailboxId)
              if (mailbox != null) {
                mailbox.removeEmailData(email.data, notiInfo, affectedCurMailbox == null)
                if (mailboxMap[mailboxId] == null) {
                  mailboxMap[mailboxId] = mailbox
                }
              }
            })
            if (affectedCurMailbox != null) {
              affectedCurMailbox.removeEmailData(email.data, notiInfo, true)
              const curMailboxId = affectedCurMailbox.getId()
              if (mailboxMap[curMailboxId] == null) {
                mailboxMap[curMailboxId] = affectedCurMailbox
              }
            }
          }
        })

        // 서버에서 destroy 된 이메일은 캐시(emailDB/emailDetailDB)에서도 제거해야
        // stale 데이터가 재조회되지 않는다.
        self.deleteEmailFromDB([
          ...destroyedEmails.list.map((emailData: EmailData) => emailData.id),
          ...destroyedEmails.notFound,
        ])

        Object.keys(mailboxMap).forEach((mailboxId) => {
          const mailbox = self.getMailboxById(mailboxId)
          if (mailbox != null) {
            mailbox.sortEmail()
            mailbox.sortThread()
          }
        })

        const eventList = self.makeEvent0(notiInfo)

        const notiEvent = eventList.filter((item) => {
          return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
        })

        self.notifyThreadChanged(notiEvent)

        self.notifyRelatedMailboxes(notiEvent)

        return { emailChanges, createdEmails, updatedEmails, destroyedEmails, info }
      })
      .catch((err: any) => {
        console.log(' -- reqUpdatedAllThreadData error : ', err)
        if (Array.isArray(err) && err.length > 0) {
          err.some((errObj) => {
            if (errObj.type === 'cannotCalculateChanges') {
              // outdated sinceState 가 발생하면 데이터를 다시 로딩해야 함.
              if (self.curMailbox != null) {
                self.curMailbox.notifyReload()
              }
              // if (self.curMailbox != null) {
              //   self.curMailbox.reqUpdatedThreadData()
              // }
              // setTimeout(() => {
              //   self.reqMailBox().then(() => {
              //     self.reqUpdatedAllThreadData()
              //   })
              // }, 100)
              return true
            }
          })
        }
        return null
      })
  }

  self.startEvtSrc = (): void => {
    const jamClient = getJamClient()
    self.isFirstOpen = true
    if (jamClient != null) {
      checkOnline()
        .then((isOnline) => {
          if (isOnline) {
            jamClient
              .connectEventSource(
                {
                  types: '*', // or ["Mailbox", "Email", ...]
                  ping: 10, // ping interval in second?.
                  closeafter: 'no', // "no" or "state"
                },
                {
                  // withCredentials: false,
                  heartbeatTimeout: 10 * 60 * 1000,
                },
              )
              .then((evtSrc: EventSourceType) => {
                self.evtSrc = evtSrc
                self.evtSrc.addEventListener('open', self.onEvtSrcOpen)
                self.evtSrc.addEventListener('message', self.onEvtSrcMessage)
                self.evtSrc.addEventListener('state', self.onChangeEvtSrcState)
                self.evtSrc.addEventListener('error', self.onEvtSrcError)
              })
          }
        })
        .catch(() => {})
    }
  }

  self.closeEvtSrc = (): void => {
    if (self.evtSrc != null) {
      self.evtSrc.removeEventListener('open', self.onEvtSrcOpen)
      self.evtSrc.removeEventListener('message', self.onEvtSrcMessage)
      self.evtSrc.removeEventListener('state', self.onChangeEvtSrcState)
      self.evtSrc.removeEventListener('error', self.onEvtSrcError)
      self.evtSrc.close()
      self.evtSrc = null
    }
  }

  self.updateAllData = (): void => {
    // mailbox 데이터를 처리한 다음, thread 데이터를 처리함.
    // 2-arg then: onRejected는 reqMailBox 실패만 처리(onFulfilled 콜백 예외는 흡수하지 않음).
    self.reqMailBox().then(
      () => {
        if (self.mailbox.list.length > 0) {
          self.reqUpdatedAllThreadData()
        }
      },
      (err: unknown) => handleJmapCallError('updateAllData', err),
    )
  }

  self.onEvtSrcOpen = async (e: any): Promise<void> => {
    console.log('EvtSrc is opened. - ', e)
    if (self.isFirstOpen == true) {
      self.isFirstOpen = false
      return
    }
    if (self.searchMailbox.isSearchMode() === true) {
      return
    }
    // 재연결 직후 updateAllData가 발사하는 다발 요청이 만료 토큰으로 401 카스케이드를
    // 일으키지 않도록, 만료된 경우에만 선제 갱신한다.
    await updateTokenIfExpired()
    self.updateAllData()
  }

  self.onVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState !== 'visible') return
    // 장시간 유휴 후 탭 복귀 시, 사용자의 첫 클릭이 만료 토큰으로 발사되어
    // 401 → refresh → retry 카스케이드를 타는 것을 방지한다.
    await updateTokenIfExpired()
  }

  self.onEvtSrcMessage = (evt: any): void => {
    console.log('evtSrt - ', evt)
  }

  self.onChangeEvtSrcState = (evt: any): void => {
    console.log('onChangeEvtSrcState - ', evt)
    if (self.searchMailbox.isSearchMode() !== true) {
      self.updateAllData()
    }
  }

  self.refreshJMAPAuthInfo = async (token: string): Promise<void> => {
    const jamClient = getJamClient()
    if (jamClient == null) return

    const newTokenType = await localforage.getItem('app.user.jmap.tokenType')
    // token 정보 갱신.
    jamClient.setAuthInfo(newTokenType, token)
  }

  self.onUpdateToken = (token: string): void => {
    self.refreshJMAPAuthInfo(token)
  }

  self.onEvtSrcError = (evt: any): void => {
    console.log('evtSrt err - ', evt)
    const { type, status } = evt
    if (type === 'error' && status === 401) {
      // token expired 상태로 간주.
      self.closeEvtSrc()
      // token 갱신.
      updateToken().then(async (token) => {
        // 갱신 성공 시에만 JMAP auth 갱신 + 재연결한다. token이 null이면(갱신 실패)
        // refreshJMAPAuthInfo(null)로 잘못된 헤더를 설정하지 않는다.
        // 참고: 갱신 성공 시 applyTokenUpdate의 notify가 이미 refreshJMAPAuthInfo를 호출하지만,
        // 재연결(startEvtSrc) 전에 authHeader 갱신 완료를 보장하기 위해 여기서 await한다.
        if (token != null) {
          await self.refreshJMAPAuthInfo(token)
          setTimeout(() => {
            self.startEvtSrc()
          }, 10)
        }
      })
    }
  }

  self.isFirstLoadCalled = false
  self.firstLoad = (callback?: () => void): void => {
    if (self.isFirstLoadCalled === false) {
      self.isFirstLoadCalled = true
      self.startEvtSrc()
      window.removeEventListener('online', self.startEvtSrc)
      window.removeEventListener('offline', self.closeEvtSrc)
      window.addEventListener('online', self.startEvtSrc)
      window.addEventListener('offline', self.closeEvtSrc)
      // Firefox는 reload 시 네비게이션 시작 시점에 진행 중인 SSE fetch를 AbortError가 아닌
      // NetworkError로 끊는데, 이 rejection은 pagehide보다 먼저 도착하므로 네비게이션 전에
      // 동기 발화하는 beforeunload에서 닫아야 정상 abort 경로를 탄다. pagehide는 보강용.
      window.removeEventListener('beforeunload', self.closeEvtSrc)
      window.removeEventListener('pagehide', self.closeEvtSrc)
      window.addEventListener('beforeunload', self.closeEvtSrc)
      window.addEventListener('pagehide', self.closeEvtSrc)
      document.removeEventListener('visibilitychange', self.onVisibilityChange)
      document.addEventListener('visibilitychange', self.onVisibilityChange)
      self.reqIdentity()
      // 2-arg then: onRejected는 reqMailBox 실패만 처리(callback 예외는 흡수하지 않음).
      self.reqMailBox().then(
        () => {
          if (callback != null) {
            callback()
          }
        },
        (err: unknown) => handleJmapCallError('firstLoad', err),
      )
    }
  }

  self.getAllDataEvent = (key: string): any => {
    let ret = null
    if (key === 'MailboxChange') {
      ret = {
        data: {
          ...self.mailbox,
        },
        action: ACTION.SET,
        type: TYPE.MAILBOX,
      }
    } else if (key === 'IdentityChange') {
      ret = {
        data: {
          ...self.identity,
        },
        action: ACTION.SET,
        type: TYPE.IDENTITY,
      }
    }
    return ret
  }

  self.getRootMailboxes = (): any[] => {
    return self.mailbox.list
  }

  self.getAllMailboxes = (): any[] => {
    return self.mailbox.allList
  }

  self.getRootMailbox = (): any => {
    if (this.rootMailbox == null) {
      const data = {
        id: '__root__',
        name: 'root',
        role: 'root',
      }
      this.rootMailbox = {
        data,
        getId: () => data.id,
        getRole: () => data.role,
        getDisplayName: () => i18n.t(`mailbox.displayName.root`),
        getName: () => data.name,
        getFullName: () => i18n.t(`mailbox.displayName.root`),
      }
    }
    return this.rootMailbox
  }

  self.getMailboxById = (id: string, realOnly: boolean = false): any => {
    if (realOnly !== true && self.searchMailbox.isSearchMode() === true) {
      const innerMailbox = self.searchMailbox.getInnerMailbox()
      if (innerMailbox == null || innerMailbox.getId() === id) {
        return self.searchMailbox
      }
    }
    if (self.normalMailboxMap[id] != null) {
      return self.normalMailboxMap[id]
    }
    if (self.filteredMailboxMap[id] != null) {
      return self.filteredMailboxMap[id]
    }
    if (id === '__root__') {
      return this.getRootMailbox()
    }
    return self.mailbox.map[id]
  }

  self.addEmail = (email: any): void => {
    self.allEmailMap[email.getId()] = email
  }

  self.removeEmail = (email: any): void => {
    if (self.allEmailMap[email.getId()] != null) {
      delete self.allEmailMap[email.getId()]
    }
  }

  // 이메일이 실제로 삭제(destroy)된 경우에만 호출. move(휴지통 이동/폴더 이동)에서는
  // email id 가 유지되고 이메일이 존재하므로 캐시를 지우면 안 된다.
  // best-effort 정리이므로 실패는 무시한다.
  self.deleteEmailFromDB = (emailIds: string[]): void => {
    emailIds.forEach((emailId) => {
      emailDB.deleteData(emailId).catch(() => {})
      emailDetailDB.deleteData(emailId).catch(() => {})
    })
  }

  self.getEmailById = (id: string): any => {
    return self.allEmailMap[id] || this.outbox.map[id]
  }

  self.getThreadById = (id: string): any => {
    if (self.allThreadMap[id] == null) {
      self.allThreadMap[id] = new ThreadObj(self, id)
    }
    return self.allThreadMap[id]
  }

  self.removeThreadById = (id: string): void => {
    delete self.allThreadMap[id]
  }

  self.getIdentityInfo = (): IdentityContainer => {
    return self.identity
  }

  self.getTrash = (): any => {
    return self.mailbox.trash
  }

  self.getDrafts = (): any => {
    return self.mailbox.drafts
  }

  self.getMailboxForRole = (role: string): any => {
    const roleStr = role.toLowerCase()
    if (self.filteredMailboxMap[roleStr] != null) {
      return self.filteredMailboxMap[roleStr]
    }
    const ret = self.mailbox.allList.find((item) => {
      const role = item.getRole()
      return role === roleStr
    })
    return ret
  }

  self.checkOpenUnread = (email: any, targetMailbox?: any): Promise<any> => {
    if (email != null && settings.getValue('email').isMarkAsReadWhenOpen === true) {
      return email.setRead(true, targetMailbox)
    }
    return Promise.resolve(null)
  }

  self.checkRemoveUnread = (emails: any[], targetMailbox?: any): Promise<any> => {
    if (settings.getValue('email').isMarkAsReadWhenDeleted === true) {
      return self.setKeyword(emails, '$seen', true, targetMailbox)
    }
    return Promise.resolve(null)
  }

  self.checkReplyUnread = (email: any, targetMailbox?: any): Promise<any> => {
    if (settings.getValue('email').isAutoMarkAsReadWhenReplyingOrForwarding === true) {
      return email.setRead(true, targetMailbox)
    }
    return Promise.resolve(null)
  }

  // known sender - 주소록 또는 조직도에 포함된 이메일.
  // 에러 경로용 짧은 negative TTL.
  // catch 블록이 putData를 생략하면 BaseCache에 placeholder(영구 생존)만 남고,
  // 동일 email 재호출 시 getData는 여전히 null을 반환해 fetch가 무한 재시도된다.
  // 10초간 false를 캐싱해 장애 구간에서의 재시도 스톰을 차단한다(정상 복구는 clearKnownSenderCache가 보장).
  const KNOWN_SENDER_ERROR_TTL = 10 * 1000

  self.checkKnownSender = async (email: string): Promise<boolean> => {
    const cached = await knownSenderCache.getData(email)
    if (cached != null) {
      return cached.data
    }

    const inflight = knownSenderInFlight.get(email)
    if (inflight != null) return inflight

    const generationAtStart = knownSenderGeneration
    const promise = (async (): Promise<boolean> => {
      try {
        const orgRet = await orgChartHelper.findContact(email)
        if (orgRet != null) {
          if (knownSenderGeneration === generationAtStart) {
            await knownSenderCache.putData(email, true)
          }
          return true
        }
        const contactRet = await contactHelper.findContact(email)
        const result = contactRet != null
        if (knownSenderGeneration === generationAtStart) {
          await knownSenderCache.putData(email, result)
        }
        return result
      } catch {
        // 네트워크/조회 오류 시 false를 짧은 TTL로 negative-cache.
        // clear()가 중간에 발생했다면 이전 세션의 에러 결과이므로 putData를 건너뛴다.
        if (knownSenderGeneration === generationAtStart) {
          await knownSenderCache.putData(email, false, KNOWN_SENDER_ERROR_TTL)
        }
        return false
      }
    })().finally(() => {
      if (knownSenderInFlight.get(email) === promise) {
        knownSenderInFlight.delete(email)
      }
    })

    knownSenderInFlight.set(email, promise)
    return promise
  }

  self.checkAllowedSender = async (email: string): Promise<boolean> => {
    const myEmail = getEnvParam('userEmail')
    if (email === myEmail) return true
    try {
      const data = await allowedSenderDB.getData(email)
      return data != null
    } catch {
      return false
    }
  }

  self.addAllowedSender = (email: string): Promise<boolean> => {
    const promise = new Promise<boolean>((resolve) => {
      allowedSenderDB
        .putData(email, email)
        .then((data) => {
          resolve(data != null)
        })
        .catch((e) => {
          console.log('addAllowedSender error1 : ', e)
          resolve(false)
        })
    })
    return promise
  }

  self.clearAllowedSenderData = (): void => {
    allowedSenderDB.clear()
  }

  self.applyNormalMailbox = (): void => {
    if (self.normalMailboxMap != null) {
      Object.values(self.normalMailboxMap).forEach((mailbox: any) => {
        if (self.mailbox.map[mailbox.getId()] == null) {
          self.mailbox.map[mailbox.getId()] = mailbox
          if (self.mailbox.list.find((item) => item.getId() === mailbox.getId()) == null) {
            self.mailbox.list.push(mailbox)
          }
          if (self.mailbox.allList.find((item) => item.getId() === mailbox.getId()) == null) {
            self.mailbox.allList.push(mailbox)
          }
        }
      })
    }
  }

  // mailbox array 또는 mailbox id array를 인수로 받는다.
  self.reqMailBox = (mailboxes?: any[]): Promise<any> => {
    let ids: string[] | null = null
    if (mailboxes != null && mailboxes.length > 0) {
      ids = mailboxes
        .map((item) => {
          if (item.id != null) {
            return item.id
          } else if (item.getId != null) {
            if (item.isRealMailbox === true) {
              return item.getId()
            } else {
              return null
            }
          }
          // string 이어야 함.
          if (typeof item === 'string') {
            return item
          }
          return null
        })
        .filter((item) => {
          return item != null
        }) as string[]
    }

    const accountId = jam.accountId

    const mailboxState = self.mailboxReq.getLastState()

    const client = getJamClient()
    if (client == null) {
      return Promise.reject(new Error('JMAP client not initialized'))
    }

    if (ids == null && mailboxState != null) {
      return client
        .requestMany((r: any) => {
          const mailboxIds = r.Mailbox.changes({
            accountId,
            sinceState: mailboxState,
          })

          const createdMailboxes = r.Mailbox.get({
            accountId,
            ids: mailboxIds.$ref('/created'),
          })

          const updatedMailboxes = r.Mailbox.get({
            accountId,
            ids: mailboxIds.$ref('/updated'),
            // properties: mailboxIds.$ref('/updatedProperties'),
          })

          const destroyedMailboxes = r.Mailbox.get({
            accountId,
            ids: mailboxIds.$ref('/destroyed'),
          })

          return { mailboxIds, createdMailboxes, updatedMailboxes, destroyedMailboxes }
        })
        .then(
          async ([{ mailboxIds, createdMailboxes, updatedMailboxes, destroyedMailboxes }]: any) => {
            console.log(' -- reqMailBox mailboxIds : ', mailboxIds)
            console.log(' -- reqMailBox createdMailboxes : ', createdMailboxes)
            console.log(' -- reqMailBox updatedMailboxes : ', updatedMailboxes)
            console.log(' -- reqMailBox destroyedMailboxes : ', destroyedMailboxes)

            if (mailboxIds.newState != null) {
              self.mailboxReq.setLastState(mailboxIds.newState)
            }

            let mailboxList: any[] = []

            if (createdMailboxes != null && createdMailboxes.list != null) {
              mailboxList = [...mailboxList, ...createdMailboxes.list]
            }

            if (updatedMailboxes != null && updatedMailboxes.list != null) {
              mailboxList = [...mailboxList, ...updatedMailboxes.list]
            }

            if (mailboxList.length > 0) {
              self.processMailBox(mailboxList, false)
            }

            if (destroyedMailboxes != null) {
              const destroyedList = [...destroyedMailboxes.list, ...destroyedMailboxes.notFound]
                .map((item) => {
                  let id: string | null = null
                  if (typeof item === 'string') {
                    id = item
                  } else if (item.id != null) {
                    id = item.id
                  }
                  if (id != null) {
                    return self.getMailboxById(id, true)
                  }
                  return null
                })
                .filter((mailbox) => {
                  return mailbox != null
                })

              destroyedList.forEach((mailbox) => {
                self.notifyMailboxDeleted(mailbox)
              })
            }

            await self.updateNormalMailbox()
            await self.updateFilteredMailbox()

            return { mailboxIds, createdMailboxes, updatedMailboxes, destroyedMailboxes }
          },
        )
    }

    return client
      .request([
        'Mailbox/get',
        {
          accountId,
          ids,
        },
      ])
      .then(async ([mailboxes]: any) => {
        console.log(' -- mailboxes : ', mailboxes)

        if (mailboxes.state != null) {
          self.mailboxReq.setLastState(mailboxes.state)
        }

        self.processMailBox(mailboxes.list, ids == null)

        // let trash = null
        // let sent = null
        // let drafts = null

        // if (self._isDefaultMailboxChecked === false && (trash == null || sent == null || drafts == null)) {
        //   const create = {};
        //   let uuid = null;
        //   if (trash == null) {
        //     uuid = createUuid()
        //     create[uuid] = {
        //       name: 'Trash',
        //       role: 'trash'
        //     }
        //   }
        //   if (sent == null) {
        //     uuid = createUuid()
        //     create[uuid] = {
        //       name: 'Sent',
        //       role: 'sent'
        //     }
        //   }
        //   if (drafts == null) {
        //     uuid = createUuid()
        //     create[uuid] = {
        //       name: 'Drafts',
        //       role: 'drafts'
        //     }
        //   }

        //   self._isDefaultMailboxChecked = true;

        //   getJamClient().request([
        //     'Mailbox/set',
        //     {
        //       accountId: jam.accountId,
        //       create
        //     },
        //   ]).then((res) => {
        //     console.log('create default mailbox : ', res)

        //     setTimeout(() => {
        //       self.reqMailBox();
        //     }, 10);
        //   });
        // }

        await self.updateNormalMailbox()
        await self.updateFilteredMailbox()

        return mailboxes
      })
  }

  self.createMailbox = (parentMailBox: any, name: string, role?: string): Promise<any> => {
    const uuid = createUuid()

    const data: any = {
      parentId: parentMailBox != null ? parentMailBox.data.id : null,
      name,
    }

    if (role != null) {
      data.role = role
    }

    return getJamClient()
      .request([
        'Mailbox/set',
        {
          accountId: jam.accountId,
          create: {
            [uuid]: data,
          },
        },
      ])
      .then(([res]: any) => {
        console.log('create mailbox : ', res)

        if (res.created != null && res.created[uuid] != null) {
          const addedItem = {
            ...res.created[uuid],
            ...data,
          }
          self.processMailBox([addedItem], false)
        } else if (res.notCreated != null) {
          const mailboxId = Object.keys(res.notCreated)[0]
          const val = res.notCreated[mailboxId] // notUpdated는 map & property.
          if (val != null) {
            return { error: val }
          }
        }
        // self.reqMailBox()

        return res
      })
  }

  self.outbox = {
    enabled: useOutbox,
    list: [],
    map: {},
    draft: {
      list: [],
      map: {},
    },
    applyOutboxData: (email: any, emailData: any) => {
      const defaultData = {
        id: emailData.id,
        messageId: emailData.messageId,
        from: emailData.from,
        to: emailData.to,
        cc: emailData.cc,
        bcc: emailData.bcc,
        subject: emailData.subject,
        reply_to: emailData.replyTo,
        received_time: emailData.receivedAt,
        keywords: emailData.keywords || {},
        preview: emailData.htmlBodyText.substring(0, 20),
      }
      applyData(defaultData, email.data)
      const { attachments, bodyIsHTML, htmlBody } = emailData

      email.detail = {
        ...defaultData,
        bodyPart: {
          hasHTMLBody: () => {
            return bodyIsHTML
          },
          getHTMLBody: () => {
            return htmlBody
          },
          isCalendar: () => {
            return false
          },
        },
        hasAttachment: attachments.length > 0,
        attachments,
      }

      self.outbox.map[emailData.id] = email
      self.outbox.list.push(email)
    },
    load: async () => {
      const result = await outboxDB.getAllData()
      const dataList = result?.dataList ?? []
      const promiseList = dataList.map((item: any) => {
        const { emailData, draftId } = item.data
        if (self.outbox.map[emailData.id] == null) {
          const email = new JMAPEmail(self, emailData.id)
          const { attachments } = emailData
          emailData.attachments = []

          attachments.forEach((attachment: any) => {
            const { name, type, cid, fileId } = attachment
            attachmentDB.getData(fileId).then((blob: Blob | null) => {
              if (blob == null) return
              const file = new File([blob], name, { type })
              const item = attachmentHelper.createAttachment(file, cid)
              emailData.attachments.push(item)
            })
          })

          self.outbox.applyOutboxData(email, emailData)
        }
        if (draftId != null) {
          if (self.outbox.draft.map[draftId] == null) {
            self.outbox.draft.map[draftId] = true
            self.outbox.draft.list.push(draftId)
          }
        }
      })

      await Promise.all(promiseList)
    },
    add: (emailData: any, attachments?: any[], draftId?: string) => {
      return new Promise((resolve, reject) => {
        const domain = location.host
        if (emailData.id == null) {
          emailData.id = createUuid() + '@' + domain
        }
        if (self.outbox.map[emailData.id] == null) {
          const promiseList: Promise<any>[] = []
          if (attachments != null && attachments.length > 0) {
            emailData.attachments = attachments.forEach((attachment, idx) => {
              const fileId = `${emailData.id}-${idx}`

              const promise = attachmentDB.addData(fileId, attachment.originalFile)
              promiseList.push(promise)

              return {
                ...attachment.file,
                fileId,
              }
            })
          }

          if (draftId != null && self.outbox.draft.map[draftId] == null) {
            self.outbox.draft.map[draftId] = true
            self.outbox.draft.list.push(draftId)
          }

          const email = new JMAPEmail(self, emailData.id)

          self.outbox.applyOutboxData(email, emailData)

          const promise = outboxDB.addData(emailData.id, {
            emailData,
            draftId,
          })

          promiseList.push(promise)

          self.outbox.changeListener.notify({
            data: emailData.id,
            action: 'add',
            type: 'emailId',
          })

          Promise.all(promiseList)
            .then((res) => {
              console.log(' - addToOutbox success - ')
              resolve(res)
            })
            .catch((err) => {
              console.log(' - addToOutbox error - ', err)
              reject(err)
            })
        } else {
          console.log(' - addToOutbox error - already exist.')
          reject(new Error('already exist'))
        }
      })
    },
    remove: (emailId: string) => {
      return new Promise((resolve, reject) => {
        if (self.outbox.map[emailId] != null) {
          const promiseList: Promise<any>[] = []

          const emailObj = self.outbox.map[emailId]
          const idx = self.outbox.list.indexOf(emailObj)
          if (idx > -1) {
            self.outbox.list.splice(idx, 1)
          }

          outboxDB.getData(emailId).then((item: any) => {
            if (item == null) return
            const { emailData, draftId } = item.data
            const { attachments } = emailData

            attachments.forEach((attachment: any) => {
              const { fileId } = attachment
              const promise = attachmentDB.deleteData(fileId)
              promiseList.push(promise)
            })

            const promise = outboxDB.deleteData(emailId)
            promiseList.push(promise)

            if (draftId != null && self.outbox.draft.map[draftId] != null) {
              delete self.outbox.draft.map[draftId]
              const idx = self.outbox.draft.list.indexOf(draftId)
              if (idx > -1) {
                self.outbox.draft.list.splice(idx, 1)
              }
            }

            self.outbox.changeListener.notify({
              data: emailId,
              action: 'remove',
              type: 'emailId',
            })

            Promise.all(promiseList)
              .then((res) => {
                console.log(' - removeFromOutbox success - ')
                resolve(res)
              })
              .catch((err) => {
                console.log(' - removeFromOutbox error - ', err)
                reject(err)
              })
          })
        } else {
          console.log(' - removeFromOutbox error - not exist.')
          reject(new Error('not exist'))
        }
      })
    },
    getList: () => {
      return self.outbox.list
    },
  } as OutboxData

  self.outbox.changeListener = new ListenerObj('OutboxChange', self.outbox)

  outboxDB.getDB().then(() => {
    self.outbox.load()
  })

  self.processMailBox = (mailBoxList: any[], isFullData?: boolean): void => {
    const ret = self.mailbox

    let isChanged = false
    // let needMakeParentList = false;

    if (isFullData !== false) {
      Object.entries(ret.map).forEach(([key]) => {
        const existItem = mailBoxList.find((item) => {
          return item.id === key
        })

        const { data } = ret.map[key]

        if (existItem == null) {
          // 전체 데이터가 들어오는 경우, 없어진 데이터들을 map, list 에서 제거.
          delete ret.map[key]
          isChanged = true

          const target = data.parentId != null ? ret.map[data.parentId] : ret
          if (target != null) {
            const idx = target.list.findIndex((item: any) => {
              return item.data.id === key
            })
            if (idx > -1) {
              target.list.splice(idx, 1)
            }
          }
        }
      })
    }

    self.applyNormalMailbox()

    const createOutbox = () => {
      const outboxObj = new JMAPMailbox(self) as any
      outboxObj.order = 3
      outboxObj.getRole = () => {
        return 'outbox'
      }
      outboxObj.getId = () => {
        return 'outbox'
      }
      outboxObj.getName = () => {
        return 'Outbox'
      }
      outboxObj.getEmailById = (id: string) => {
        return self.outbox.map[id]
      }
      outboxObj.getAllData = () => {
        return {
          emailList: self.outbox.list,
        }
      }
      outboxObj.deleteOutboxMails = async (emailList: any[]) => {
        emailList.forEach((email) => {
          const list = self.outbox.getList()
          const existEmail = list.find((item) => item.data.id === email.data.id)
          if (existEmail != null) {
            self.outbox.remove(existEmail.data.id)
          }
        })

        let outboxRet = null
        const drafts = dataManager.getMailboxForRole('drafts')
        const trash = dataManager.getMailboxForRole('trash')
        if (drafts != null && emailList != null && emailList.length > 0) {
          if (trash != null) {
            outboxRet = drafts.move(emailList, trash)
          } else {
            outboxRet = drafts.deleteEmails(emailList)
          }
        }

        return outboxRet
      }

      return outboxObj
    }

    const addedIdMap: { [key: string]: boolean } = {}

    const createMailboxObj = (map: { [key: string]: any }, data: any, useParent: boolean) => {
      const mailboxObj = new JMAPMailbox(self)
      if (useParent === true) {
        map[data.parentId] = mailboxObj
        addedIdMap[data.parentId] = true
      } else {
        map[data.id] = mailboxObj
        addedIdMap[data.id] = true
      }

      if ((useOutbox as any) === true && data.role === 'drafts' && map['outbox'] == null) {
        map['outbox'] = createOutbox()
      }
    }

    const applyData = (map: { [key: string]: any }, data: any): boolean => {
      if (data.role === 'drafts' && map['outbox'] != null) {
        map['outbox'].applyData(data)
      }

      return map[data.id].applyData(data)
    }

    const pushMailbox = (list: any[], map: { [key: string]: any }, data: any) => {
      list.push(map[data.id])
      if (data.role === 'drafts' && map['outbox'] != null) {
        list.push(map['outbox'])
      }
    }

    mailBoxList.forEach((item /* , idx */) => {
      const data = item

      // console.log(' ---- mailbox : ', idx, data)

      if (ret.map[data.id] == null) {
        createMailboxObj(ret.map, data, false)
        isChanged = true
      }

      if (applyData(ret.map, data) === true) {
        isChanged = true
      }

      let parentList = ret.list
      if (data.parentId != null) {
        if (ret.map[data.parentId] == null) {
          createMailboxObj(ret.map, data, true)
        }

        const parent = ret.map[data.parentId]
        if (ret.parentMap[data.id] != null && ret.parentMap[data.id] !== parent) {
          const idx = ret.parentMap[data.id].list.findIndex((item: any) => {
            return item.data.id === data.id
          })
          if (idx > -1) {
            ret.parentMap[data.id].list.splice(idx, 1)
          }
        }
        ret.parentMap[data.id] = parent

        if (parent.list == null) {
          parent.list = []
        }
        parentList = parent.list
      } else if (ret.parentMap[data.id] != null) {
        const parent = ret.parentMap[data.id]
        parentList = parent.list
      }

      const isExistInParentList = parentList.some((item) => {
        return item.data.id === data.id
      })

      if (isExistInParentList !== true) {
        pushMailbox(parentList, ret.map, data)
      }

      const isExistInAllList = ret.allList.some((item) => {
        return item.data.id === data.id
      })

      if (isExistInAllList !== true) {
        pushMailbox(ret.allList, ret.map, data)
      }

      const role = ret.map[data.id].getRole()
      if (role === 'trash') {
        self.mailbox.trash = ret.map[data.id]
      } else if (role === 'drafts') {
        self.mailbox.drafts = ret.map[data.id]
      }
    })

    if (useScheduledEmail === true) {
      const schedule = self.getMailboxById('schedule')
      if (schedule != null) {
        schedule.order = 5
      }
    }

    if (self.appServices.spam_sniper === true) {
      const spamsniper = self.getMailboxById('spamsniper')
      if (spamsniper != null) {
        spamsniper.order = 6
      }
    }

    mailBoxList.forEach((item) => {
      const mailbox = self.getMailboxById(item.id)
      const role = mailbox.getRole()
      if (role === 'inbox') {
        mailbox.order = 1
      } else if (role === 'archive') {
        mailbox.order = 8
      } else if (role === 'junk') {
        mailbox.order = 9
      } else if (role === 'trash') {
        mailbox.order = 7
        // trash = item
      } else if (role === 'sent') {
        mailbox.order = 4
        // sent = item
      } else if (role === 'drafts') {
        mailbox.order = 2
        // drafts = item
      } else {
        mailbox.order = 10
      }
    })

    self.sortMailbox()

    if (isChanged) {
      if (isFullData === true) {
        self.notifyAllMailboxChanged()
      } else {
        const addedMailboxes: any[] = []
        const updatedMailboxes: any[] = []
        mailBoxList.forEach((item) => {
          const mailbox = self.mailbox.map[item.id]
          if (addedIdMap[item.id] === true) {
            addedMailboxes.push(mailbox)
          } else {
            updatedMailboxes.push(mailbox)
          }
        })
        if (addedMailboxes.length > 0) {
          self.notifyMailboxAdded(addedMailboxes)
        }
        if (updatedMailboxes.length > 0) {
          self.notifyMailboxUpdated(updatedMailboxes)
        }
      }
    }
  }

  self.sortMailbox = (mailboxList?: any[]): void => {
    const locale = settings.getLocale()

    const sortList = (mList: any[]) => {
      mList.forEach((item) => {
        if (item.list != null) {
          sortList(item.list)
        }
      })

      mList.sort((aObj, bObj) => {
        if (aObj.order > bObj.order) {
          return 1
        } else if (aObj.order < bObj.order) {
          return -1
        }
        const a = aObj.data
        const b = bObj.data
        const aName = a.name.toLowerCase()
        const bName = b.name.toLowerCase()
        // 언어에 따라 정렬 순서가 달라지므로 localeCompare를 사용한다.
        return aName.localeCompare(bName, locale)
      })
    }

    if (mailboxList == null) {
      const { list } = self.mailbox
      sortList(list)
    } else {
      sortList(mailboxList)
    }
  }

  self.notifyThreadChanged = (notiEvent: EventObj[]): void => {
    self.threadChangeListener.notify(notiEvent)
    if (notiEvent != null && notiEvent.length > 0) {
      self.updateFilteredMailbox()
    }
  }

  // self.notifyReload = () => {
  //   self.mailbox.changeListener.notify({
  //     target: self,
  //     targetType: TYPE.DATAMANAGER,
  //     action: ACTION.RELOAD,
  //     type: TYPE.MAILBOX,
  //   })
  // }

  self.notifyMailboxAdded = (addedMailboxes: any[]): void => {
    self.mailbox.changeListener.notify({
      data: {
        list: addedMailboxes,
      },
      action: ACTION.ADD,
      type: TYPE.MAILBOX,
    })
  }

  self.notifyMailboxUpdated = (updatedMailboxes: any[]): void => {
    self.mailbox.changeListener.notify({
      data: {
        list: updatedMailboxes,
      },
      action: ACTION.UPDATE,
      type: TYPE.MAILBOX,
    })
    self.tryMigrateLegacyFavoriteMailboxes()
    self.tryNormalizeFavoriteEntries()
  }

  self.notifyAllMailboxChanged = (): void => {
    self.mailbox.changeListener.notify({
      data: {
        list: self.mailbox.list,
        map: self.mailbox.map,
      },
      action: ACTION.SET,
      type: TYPE.MAILBOX,
    })
    self.tryMigrateLegacyFavoriteMailboxes()
    self.tryNormalizeFavoriteEntries()
  }

  self.notifyMailboxDeleted = (mailbox: any): void => {
    const { data } = mailbox
    if (self.mailbox.map[data.id] != null) {
      const parentList =
        data.parentId != null ? self.mailbox.map[data.parentId].list : self.mailbox.list
      const idx = parentList.findIndex((item: any) => {
        return item.data.id === data.id
      })
      if (idx > -1) {
        parentList.splice(idx, 1)
      }
      delete self.mailbox.map[data.id]
    }

    self.mailbox.changeListener.notify({
      data: {
        item: mailbox,
      },
      action: ACTION.DELETE,
      type: TYPE.MAILBOX,
    })
  }

  self.createEmail = (
    mailData: MailData,
    previousDraft?: any,
    sendMail?: boolean,
  ): Promise<any> => {
    mailData.draftsBox = self.getMailboxForRole('drafts')
    mailData.sentBox = self.getMailboxForRole('sent')

    if (this.identity.default != null) {
      mailData.identityId = this.identity.default.id
    }

    return createEmailFunc(mailData, previousDraft, sendMail).then(
      ({
        createdEmail,
        emailSubmissionSet,
        emailChanges,
        mailboxChanges,
        mailboxGet1,
        mailboxGet2,
        emailGet,
      }) => {
        console.log(' -- createdEmail : ', createdEmail)
        console.log(' -- emailSubmissionSet : ', emailSubmissionSet)
        console.log(' -- emailChanges : ', emailChanges)
        console.log(' -- mailboxChanges : ', mailboxChanges)
        console.log(' -- mailboxGet1 : ', mailboxGet1)
        console.log(' -- mailboxGet2 : ', mailboxGet2)
        console.log(' -- emailGet : ', emailGet)

        // 결과를 보자~!

        // emailChanges.created.forEach((emailId) => {
        //   let email = this.getEmailById(emailId);
        //   if (email == null) {
        //     email = new JMAPEmail(this, emailId);
        //   }
        //   email.load(false);
        // });

        // emailChanges.updated.forEach((emailId) => {
        //   const email = this.getEmailById(emailId);
        //   email.load(false);
        // });

        // emailChanges.deleted.forEach((emailId) => {
        //   const email = this.getEmailById(emailId);
        //   email.data.mailboxIds.forEach((mailboxId) => {
        //     const mailbox = this.getMailboxById(mailboxId);
        //     mailbox.applyEmailDeletedFromServer([emailId], notiInfo);
        //   });
        // });

        const changeCreadedKeys = emailChanges.created

        // 현재 보여지고 있는 mailbox의 threadlist는 새로 update.
        // mailboxGet1, mailboxGet2의 내용은 바로 반영.

        const notiInfo: NotiInfo = {}

        // if (sendMail !== true) {
        //   self.addEvent0Obj(
        //     notiInfo,
        //     TYPE.EMAIL,
        //     ACTION.SAVE,
        //     { type: TYPE.MAILBOX, id: mailData.draftsBox.getId() },
        //     mailData.draftsBox,
        //   )
        // }

        const mailboxMap: { [key: string]: any } = {}
        // const addedMailboxMap = {}
        // const deletedMailboxMap = {}

        if (mailboxGet2.list != null) {
          mailboxGet2.list.forEach((item: any) => {
            const mailbox = self.getMailboxById(item.id)
            mailboxMap[item.id] = mailbox

            if (mailbox.applyData(item) === true) {
              self.mailbox.changeListener.notify({
                action: ACTION.UPDATE,
                type: TYPE.MAILBOX,
                data: {
                  item: mailbox,
                },
              })
            }
          })
        }

        let emailObjForAiSummary: any = null

        let newEmailId: string | null = null

        if (emailGet.list != null && emailGet.list.length > 0) {
          emailGet.list.forEach((emailData: EmailData) => {
            newEmailId = emailData.id
            const mailboxKeys = Object.keys(emailData.mailboxIds)
            let emailObj = null
            if (mailboxKeys.length > 0) {
              mailboxKeys.forEach((mailboxId) => {
                const mailbox = self.getMailboxById(mailboxId)
                if (mailbox != null) {
                  emailObj = mailbox.addEmailData(emailData, notiInfo, false)
                }
              })
            } else {
              emailObj = self.applyEmailData(emailData, notiInfo, false)
            }
            if (sendMail === true && emailObj != null) {
              emailObjForAiSummary = emailObj
            }
          })

          if (emailSubmissionSet != null) {
            if (changeCreadedKeys.length === 1) {
              const createdItem = emailGet.list.find((item) => {
                return item.id === changeCreadedKeys[0]
              })
              if (createdItem != null) {
                const myEmail = getEnvParam('userEmail')
                const { from, to, cc, bcc } = createdItem
                if (
                  from != null &&
                  from.length > 0 &&
                  (from[0] === myEmail || from[0].email === myEmail)
                ) {
                  const target = [...(to || []), ...(cc || []), ...(bcc || [])]
                  const existItem = target.find((address) => {
                    return address != null && (address === myEmail || address.email === myEmail)
                  })
                  if (existItem != null) {
                    // 생성된 이메일이 1개인 경우, 받은 사람과 보낸 사람이 모두 user 자신이면, 1개가 아직 오지 않은 상태이므로 바로 다시 로딩한다.
                    // ex) 기본적으로 자신에게 메일을 발송하면, 받은편지함, 보낸편지함에 같이 메일이 추가되어야 함.
                    setTimeout(() => {
                      self.reqUpdatedAllThreadData()
                    }, 500)
                  }
                }
              }
            }
          }
        }

        if (createdEmail != null && createdEmail.destroyed != null) {
          createdEmail.destroyed.forEach((emailId: string) => {
            const emailObj = self.getEmailById(emailId)
            if (newEmailId != null) {
              // draft인 경우 변경된 emailId를 전달.
              emailObj.newEmailId = newEmailId
            }
            if (emailObj != null && emailObj.data.mailboxIds != null) {
              Object.keys(emailObj.data.mailboxIds).forEach((mailboxId) => {
                const mailbox = self.getMailboxById(mailboxId)
                mailbox.removeEmailData(emailObj.data, notiInfo, true)
              })
            }
          })
          // draft 전송 등으로 기존 이메일이 destroy 된 경우 캐시도 제거.
          self.deleteEmailFromDB(createdEmail.destroyed)
        }

        // EmailSubmission이 실제로 성공(created)했을 때만 로컬 상태를 sent로 반영한다.
        // 서버 onSuccessUpdateEmail은 created된 submission에만 적용되므로, notCreated(발송 실패,
        // 예: invalidRecipients)인 경우 서버는 draft를 그대로 두는데 클라이언트만 sent로 옮기면
        // 상태가 어긋난다. (emailSubmissionSet != null 만으로는 실패도 통과하므로 created 확인 필요.)
        const submissionSucceeded =
          emailSubmissionSet != null &&
          emailSubmissionSet.created != null &&
          Object.keys(emailSubmissionSet.created).length > 0
        if (createdEmail == null && submissionSucceeded) {
          // 서버 onSuccessUpdateEmail은 previousDraft를 sent로 이동 + $draft 제거한다.
          // emailChanges.updated 포함 여부(공유 jam.lastState 동기화 타이밍)에 의존하지 않고
          // previousDraft.id를 직접 결정론적으로 로컬 반영하여 서버 상태와 일치시킨다.
          const email = previousDraft != null ? this.getEmailById(previousDraft.id) : null
          if (email != null && email.data != null && mailData.sentBox != null) {
            const nextKeywords = { ...email.data.keywords }
            delete nextKeywords.$draft // 서버 onSuccessUpdateEmail의 'keywords/$draft': null 과 일치
            const tmpData = {
              ...email.data,
              keywords: nextKeywords, // 원본 keywords 참조 공유 방지 (applyData는 키 단위 전체 교체)
              mailboxIds: {
                [mailData.sentBox.getId()]: true,
              },
            }

            const emailObj = self.applyEmailData(tmpData, notiInfo, false)

            if (sendMail === true && emailObj != null) {
              emailObjForAiSummary = emailObj
            }
          }
        }

        // AiSummary 요청 한번만 보내도록 수정.
        if (emailObjForAiSummary != null) {
          createAiSummaryForNew(emailObjForAiSummary, mailData.htmlText)
        }

        const eventList = self.makeEvent0(notiInfo)
        const notiEvent = eventList.filter((item) => {
          return item.type === TYPE.THREAD || item.type === TYPE.EMAIL
        })

        self.notifyRelatedMailboxes(notiEvent)

        // AiSummary가 off 상태인 경우에도 EmailThreadViewer를 갱신할 수 있도록 호출.
        eventList.forEach((item) => {
          if (item.type === TYPE.EMAIL && item.targetType === TYPE.DATAMANAGER) {
            self.emailChangeListener.notify(item)
          }
        })

        // mailbox 가 새로 생성되는 경우는 발생하지 않음.
        // @todo - 특수한 예외 경우가 발생하는 경우에는 아래의 코드에서 mailbox created를 구현해야 함.
        // let isChanged = false
        // // const changedMap = {};

        // if (mailboxGet1.list != null) { // mailbox created.
        //   mailboxGet1.list.forEach((item) => {
        //     const mailbox = self.mailbox.map[item.id]
        //     if (mailbox.applyData(item) === true) {
        //       // changedMap[item.id] = mailbox;
        //       isChanged = true
        //     }
        //   })
        // }
        // if (mailboxGet2.list != null) {
        //   mailboxGet2.list.forEach((item) => {
        //     const mailbox = self.mailbox.map[item.id]
        //     if (mailbox.applyData(item) === true) {
        //       // changedMap[item.id] = mailbox;
        //       isChanged = true
        //     }
        //   })
        // }

        // // Object.entries(changedMap).forEach(([key, value]) => {

        // // })

        // if (isChanged === true) {
        //   self.mailbox.changeListener.notify({
        //     data: {
        //       list: self.mailbox.list,
        //       map: self.mailbox.map,
        //     },
        //     action: ACTION.SET,
        //     type: TYPE.MAILBOX,
        //   })
        // }

        return {
          createdEmail,
          emailSubmissionSet,
          emailChanges,
          mailboxChanges,
          mailboxGet1,
          mailboxGet2,
          emailGet,
        }
      },
    )
  }

  self.notifyRelatedMailboxes = (notiEvent: EventObj[]): void => {
    const mailboxMap: { [key: string]: any } = {}
    notiEvent.forEach((event) => {
      if (event.targetType === TYPE.MAILBOX) {
        mailboxMap[event.target.getId()] = event.target
      }
    })

    Object.keys(mailboxMap).forEach((key) => {
      const mailbox = mailboxMap[key]
      mailbox.sortEmail()
      mailbox.sortThread()

      const selfNoti = notiEvent.filter((event) => {
        return event.targetType === TYPE.MAILBOX && event.target.getId() === mailbox.getId()
      })

      if (selfNoti.length > 0) {
        mailbox.threadChangeListener.notify(selfNoti)
      }
    })
  }

  self.getEMAILSTATEKEY = (subkey: string, value: boolean): number => {
    if (subkey === '$seen') {
      return value === true ? EMAIL_STATE.READING : EMAIL_STATE.UNREADING
    } else if (subkey === '$flagged') {
      return value === true ? EMAIL_STATE.FLAGGING : EMAIL_STATE.UNFLAGGING
    }
    return EMAIL_STATE.NORMAL
  }

  // targetMailbox = 현재 보여지고 있는 mailbox
  self.setKeyword = (
    emailArray: any[],
    subkey: string,
    val: boolean,
    targetMailbox?: any,
  ): Promise<boolean> => {
    // thread or email
    const data: any[] = []

    const map: { [key: string]: any } = {}

    const subkeyStr = `keywords/${subkey}`

    const emailState = self.getEMAILSTATEKEY(subkey, val)

    let enableChangeEmails: any[] = []

    if (emailState === 0) {
      enableChangeEmails = emailArray.filter((emailObj) => {
        return emailObj != null && emailObj.data.keywords[subkey] !== val
      })
    } else {
      enableChangeEmails = emailArray.filter((emailObj) => {
        if (emailObj != null) {
          switch (subkey) {
            case '$seen':
              if (
                emailState === EMAIL_STATE.READING &&
                emailObj.isMailState(EMAIL_STATE.UNREADING) === true
              ) {
                return true
              } else if (
                emailState === EMAIL_STATE.UNREADING &&
                emailObj.isMailState(EMAIL_STATE.READING) === true
              ) {
                return true
              }
            case '$flagged':
              if (
                emailState === EMAIL_STATE.FLAGGING &&
                emailObj.isMailState(EMAIL_STATE.UNFLAGGING) === true
              ) {
                return true
              } else if (
                emailState === EMAIL_STATE.UNFLAGGING &&
                emailObj.isMailState(EMAIL_STATE.FLAGGING) === true
              ) {
                return true
              }
          }
          return emailObj.data.keywords[subkey] !== val
        }
        return false
      })
    }

    if (enableChangeEmails.length === 0) {
      return Promise.resolve(true)
    }

    enableChangeEmails.forEach((emailObj) => {
      const item = emailObj.data
      map[item.id] = emailObj

      if (emailState > 0) {
        emailObj.setMailState(EMAIL_STATE.KEYWORD_MASK, false)
        emailObj.setMailState(emailState, true)
      }

      data.push({
        id: item.id,
        [subkeyStr]: val === true ? true : null,
      })
    })

    const clearEmailState = () => {
      if (emailState > 0) {
        enableChangeEmails.forEach((emailObj) => {
          emailObj.setMailState(emailState, false)
        })
      }
    }

    // return new Promise((resolve) => {
    //   resolve(true)
    // })

    return jamUpdate('Email', data)
      .then(([res]: any) => {
        console.log('setKeyword : ', subkey, res)
        if (res.updated != null) {
          const mailboxList: any[] = []
          const emails: any[] = []
          Object.keys(res.updated).forEach((key) => {
            const email = map[key]
            const { data: emailData } = email
            emailData.keywords[subkey] = val === true

            emails.push(email)

            if (email.reactMgr != null) {
              email.reactMgr.updateUI()
            }

            Object.entries(emailData.mailboxIds).forEach(([key1]) => {
              const mailboxObj = self.mailbox.map[key1]
              const emailItem = mailboxObj.getEmailById(key)
              mailboxList.push(mailboxObj)
              if (emailItem != null) {
                emailItem.data.keywords[subkey] = val === true

                mailboxObj.threadChangeListener.notify([
                  {
                    target: mailboxObj,
                    targetType: TYPE.MAILBOX,
                    action: ACTION.UPDATE,
                    type: TYPE.EMAIL,
                    data: {
                      list: [emailItem],
                    },
                  },
                ])
              }
            })
          })

          this.emailChangeListener.notify({
            target: self,
            targetType: TYPE.DATAMANAGER,
            action: ACTION.UPDATE,
            type: TYPE.EMAIL,
            data: {
              list: emails,
            },
          })

          if (mailboxList.length > 0) {
            self
              .reqMailBox(mailboxList)
              .catch((err: unknown) => handleJmapCallError('setKeyword', err))
          }

          // filtered mailbox 일 때 처리.
          // mailbox가 보여지는 상태에서는 변경 후 바로 리스트에 반영되면 안됨.
          // mailbox가 변경되거나 sync 되는 경우에만 반영되도록 해야 함.
          if (subkey === '$seen') {
            if (targetMailbox !== self.filteredMailboxMap.unread) {
              self.filteredMailboxMap.unread.reqUpdatedThreadData()
            } else {
              self.filteredMailboxMap.unread.addDelayedData(emails)
            }
          }
          if (subkey === '$flagged') {
            if (targetMailbox !== self.filteredMailboxMap.flagged) {
              self.filteredMailboxMap.flagged.reqUpdatedThreadData()
            } else {
              self.filteredMailboxMap.flagged.addDelayedData(emails)
            }
          }
          return true
        }
        return false
      })
      .finally(() => {
        clearEmailState()
      })
  }

  // mailbox array 또는 mailbox id array를 인수로 받는다.
  self.reqIdentity = (ids?: string[]): void => {
    getJamClient()
      .request([
        'Identity/get',
        {
          accountId: jam.accountId,
          ids,
        },
      ])
      .then(([identities]: any) => {
        console.log(' -- identities : ', identities)
        self.processIdentity(identities.list, ids == null)
      })
      .catch((e: any) => {
        console.log(' - Identity/get - ', e)
      })
  }

  self.processIdentity = (list: any[], isFullData?: boolean): void => {
    const ret = self.identity

    let isChanged = false
    // let needMakeParentList = false;

    if (isFullData !== false) {
      Object.entries(ret.map).forEach(([key]) => {
        const existItem = list.find((item) => {
          return item.id === key
        })

        const data = ret.map[key]

        if (existItem == null) {
          // 전체 데이터가 들어오는 경우, 없어진 데이터들을 map, list 에서 제거.
          delete ret.map[key]
          isChanged = true

          const target = data.parentId != null ? ret.map[data.parentId] : ret
          if (target != null) {
            const idx = target.list.findIndex((item: any) => {
              return item.id === key
            })
            if (idx > -1) {
              target.list.splice(idx, 1)
            }
          }
        }
      })
    }

    list.forEach((item, idx) => {
      const data = item

      console.log(' ---- identity : ', idx, data)

      if (ret.map[data.id] == null) {
        ret.map[data.id] = {}
        ret.list.push(ret.map[data.id])
        isChanged = true
      }

      applyData(data, ret.map[data.id])

      if (applyData(data, ret.map[data.id]) === true) {
        isChanged = true
      }

      if (data.mayDelete === false && ret.default == null) {
        ret.default = ret.map[data.id]
      }
    })

    // sortList(ret.list)

    if (isChanged) {
      self.identity.changeListener.notify({
        data: {
          list: ret.list,
          map: ret.map,
        },
        action: ACTION.SET,
        type: TYPE.IDENTITY,
      })
    }
  }

  self.restore = (messages: any[]): Promise<any> => {
    const [email] = messages

    const message = email.data
    let target = null
    if (message.isDraft() === true) {
      // restore 시에는 $draft 키워드를 사용.
      target = self.getMailboxForRole('drafts')
    } else {
      // if (identities...??) { // identities가 있으면 sent에서 온 메일임.
      //   target = getMailboxForRole('sent')
      // } else {
      target = self.getMailboxForRole('inbox')
      // }
    }

    const trash = self.getTrash()

    return trash.move(messages, target, true)
  }

  // 구버전 로컬 즐겨찾기 → 서버 settings 이전 상태(세션당 1회). running 중 중복 dispatch 를 막고,
  // saga 가 판정을 못 하면(서버 로드 loaded 아님/오류) pending 으로 되돌려 다음 메일함 변경 때 재시도한다.
  let favoriteMigrationState: 'pending' | 'running' | 'done' = 'pending'
  // 저장 중(Redux 반영 전)인 최신 즐겨찾기 값. CHANGE_SETTING 반영이 비동기라, 이름 변경 직후 제거처럼
  // 연속 저장이 이전 값 위에서 계산되지 않도록 읽기 기준으로 우선 사용한다.
  let pendingFavoriteEntries: string[] | null = null
  // 저장값 정규화(구 id 형식 → 경로) 마지막 저장 결과. 같은 결과를 반복 저장하지 않도록 막는다.
  let lastNormalizedFavoriteJson: string | null = null

  self.infoDBKey = {} as InfoDBKey
  self.privateInfoDBKey = ['recentEmailList']

  self.createInfoDBKeys = (email: string): void => {
    let userName: string | number = email
    if (__ENV_PRODUCTION__) {
      userName = stringToHash(email)
    }

    // 사용자(키)가 바뀌면 구버전 즐겨찾기 이전도 새 사용자 기준으로 다시 시도한다.
    favoriteMigrationState = 'pending'
    lastNormalizedFavoriteJson = null

    self.infoDBKey = {
      recentEmailList: `${userName}-recentEmailList`,
      recentMailboxList: `${userName}-recentMailboxList`,
      // 구버전 로컬 즐겨찾기 키. 서버 settings(email.favoriteMailboxes) 1회 이전용으로만 유지한다.
      favoriteMailboxList: `${userName}-favoriteMailboxList`,
      mailboxExpandedKeys: `${userName}-mailboxExpandedKeys`,
    }
  }

  self.deletePrivateDataFromInfoDB = (): void => {
    self.privateInfoDBKey.forEach((key: string) => {
      const dbKey = self.infoDBKey[key]
      if (dbKey != null) {
        infoDB.removeItem(dbKey)
      }
    })
  }

  self.recentMailboxListLimit = 10

  self.getRecentMoveMailboxIdList = (): Promise<string[]> => {
    return infoDB.getItem(self.infoDBKey.recentMailboxList)
  }

  self.addRecentMoveMailboxId = (mailboxId: string): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      try {
        infoDB.getItem(self.infoDBKey.recentMailboxList).then((res: any) => {
          const list = res || []
          const pos = list.indexOf(mailboxId)
          if (pos >= 0) {
            list.splice(pos, 1)
          }

          list.unshift(mailboxId)
          if (list.length > self.recentMailboxListLimit) {
            list.length = self.recentMailboxListLimit
          }

          infoDB.setItem(self.infoDBKey.recentMailboxList, list).then(() => {
            resolve(list)
          })
        })
      } catch (e) {
        console.log(' error addMoveMailboxId : ', e)
        reject(e)
      }
    })
  }

  self.recentEmailListLimit = 20

  self.getRecentSendEmailList = (): Promise<EmailInfo[]> => {
    return infoDB.getItem(self.infoDBKey.recentEmailList)
  }

  self.addRecentSendEmails = (emailInfoArray: EmailInfo[]): Promise<EmailInfo[]> => {
    return new Promise((resolve, reject) => {
      try {
        infoDB.getItem(self.infoDBKey.recentEmailList).then((res: any) => {
          const list = res || []

          emailInfoArray.forEach((emailInfo) => {
            let pos = -1
            while (
              (pos = list.findIndex((item) => {
                return item.email === emailInfo.email
              })) >= 0
            ) {
              list.splice(pos, 1)
            }

            list.unshift(emailInfo)
          })

          if (list.length > self.recentEmailListLimit) {
            list.length = self.recentEmailListLimit
          }

          infoDB.setItem(self.infoDBKey.recentEmailList, list).then(() => {
            resolve(list)
          })
        })
      } catch (e) {
        console.log(' error addRecentSendEmails : ', e)
        reject(e)
      }
    })
  }

  self.removeRecentSendEmail = (email: string): Promise<EmailInfo[]> => {
    return new Promise((resolve, reject) => {
      try {
        infoDB.getItem(self.infoDBKey.recentEmailList).then((res: any) => {
          const list = res || []

          let pos = -1
          while (
            (pos = list.findIndex((item) => {
              return item.email === email
            })) >= 0
          ) {
            list.splice(pos, 1)
          }

          if (list.length > self.recentEmailListLimit) {
            list.length = self.recentEmailListLimit
          }

          infoDB.setItem(self.infoDBKey.recentEmailList, list).then(() => {
            resolve(list)
          })
        })
      } catch (e) {
        console.log(' error removeRecentSendEmail : ', e)
        reject(e)
      }
    })
  }

  // 즐겨찾기 메일함은 서버 settings(email.favoriteMailboxes)에 웹·데스크탑 공용 항목(canonical 경로 또는
  // 'virtual:<id>')으로 저장한다. 값이 없으면(미커스터마이즈) role 기반 기본값을 계산해 보여줄 뿐 서버에
  // 저장하지 않는다. 해석되지 않는 항목(삭제된 폴더, 다른 기기에서 이름을 바꾼 폴더)은 숨길 뿐 지우지 않는다.
  // @see redux/settings/favoriteMailboxes

  const getStoredFavoriteEntries = (): string[] | undefined => {
    if (pendingFavoriteEntries != null) return pendingFavoriteEntries
    const stored = settings.getValue('email')?.favoriteMailboxes
    return Array.isArray(stored) ? stored : undefined
  }

  self.getFavoriteEntryMaps = (): FavoriteEntryMaps => {
    const mailboxes = [...self.getAllMailboxes(), ...Object.values(self.filteredMailboxMap)]
    return buildFavoriteEntryMaps(mailboxes, (mailbox) => toImapPath(mailbox, self))
  }

  // 메일함 → 즐겨찾기 항목. 비실 메일함은 'virtual:<id>', 실 메일함은 canonical 경로, 경로가 없으면 null.
  self.getFavoriteEntryForMailbox = (mailbox: any): string | null => {
    if (mailbox == null) return null
    if (mailbox.isRealMailbox !== true) return toVirtualFavoriteEntry(mailbox.getId())
    const path = toImapPath(mailbox, self)
    return path !== '' ? path : null
  }

  self.getMailboxForFavoriteEntry = (
    entry: string,
    maps: FavoriteEntryMaps = self.getFavoriteEntryMaps(),
  ): any => {
    const mailboxId = resolveFavoriteEntryToId(entry, maps)
    return mailboxId != null ? self.getMailboxById(mailboxId, true) : null
  }

  self.getDefaultFavoriteEntries = (): string[] => {
    const defaults = [
      self.getMailboxForRole('unread'),
      self.getMailboxForRole('flagged'),
      self.getMailboxForRole('inbox'),
      self.getMailboxForRole('drafts'),
      self.getMailboxForRole('sent'),
      self.getTrash(),
    ]
    return defaults
      .map((mailbox) => self.getFavoriteEntryForMailbox(mailbox))
      .filter((entry): entry is string => entry != null)
  }

  self.tryMigrateLegacyFavoriteMailboxes = (): void => {
    if (favoriteMigrationState !== 'pending' || isPopupMode()) return
    const localKey = self.infoDBKey.favoriteMailboxList
    if (localKey == null) return
    // 실 메일함(inbox)이 아직 없으면 기본값 계산이 불완전하므로 다음 로드에서 재시도한다.
    if (self.getMailboxForRole('inbox') == null) return

    favoriteMigrationState = 'running'
    settings.getStore().dispatch({
      type: 'settings/MIGRATE_FAVORITE_MAILBOXES',
      payload: {
        localKey,
        defaultEntries: self.getDefaultFavoriteEntries(),
        toEntries: (legacyIds: string[]) =>
          convertLegacyIdsToEntries(legacyIds, self.getFavoriteEntryMaps()),
        onComplete: (completed: boolean) => {
          favoriteMigrationState = completed ? 'done' : 'pending'
        },
      },
    })
  }

  // 서버 값이 구 형식(mailbox id)이면 canonical 항목으로 1회 교정한다(내부 테스트 계정에만 존재).
  // 정규화는 멱등이라 교정 후에는 changed=false 로 끝나며, 같은 결과는 다시 저장하지 않는다.
  // 메일함 목록 로드 후(inbox 존재) + 서버 로드 loaded 상태에서만 저장해 OMD-225 게이트를 지킨다.
  self.tryNormalizeFavoriteEntries = (): void => {
    if (isPopupMode()) return
    if (self.getMailboxForRole('inbox') == null) return
    if (settingSyncService.getServerLoadState() !== 'loaded') return
    const stored = getStoredFavoriteEntries()
    if (stored == null) return

    const { entries, changed } = normalizeFavoriteEntries(stored, self.getFavoriteEntryMaps())
    if (!changed) return
    const normalizedJson = JSON.stringify(entries)
    if (normalizedJson === lastNormalizedFavoriteJson) return
    lastNormalizedFavoriteJson = normalizedJson
    saveFavoriteEntries(entries).catch((err: Error) => {
      console.error('Failed to normalize favorite mailboxes:', err)
    })
  }

  self.getFavoriteEntryList = (): string[] => {
    return resolveFavoriteEntries(getStoredFavoriteEntries(), self.getDefaultFavoriteEntries())
  }

  // 현재 메일함으로 해석되는 즐겨찾기 메일함 목록(표시 순서). 해석되지 않는 항목은 건너뛴다.
  self.getFavoriteMailboxes = (): any[] => {
    const maps = self.getFavoriteEntryMaps()
    return self
      .getFavoriteEntryList()
      .map((entry) => self.getMailboxForFavoriteEntry(entry, maps))
      .filter((mailbox) => mailbox != null)
  }

  const saveFavoriteEntries = async (entries: string[]): Promise<void> => {
    pendingFavoriteEntries = entries
    try {
      await settings.setValue('email.favoriteMailboxes', entries)
    } finally {
      if (pendingFavoriteEntries === entries) pendingFavoriteEntries = null
    }
    // 즐겨찾기는 Settings 팝업 Save(onOk)를 거치지 않으므로 email 카테고리를 명시적으로 서버에 push 한다.
    // 서버 로드(loaded) 이전이면 settingSync 게이트가 drop 하고 loaded 전이 시 reconciliation 으로 재전송한다.
    settings.getStore().dispatch({ type: 'settings/UPLOAD_SETTINGS', payload: { email: true } })
  }

  self.addFavoriteMailboxId = async (mailboxId: string): Promise<void> => {
    const entry = self.getFavoriteEntryForMailbox(self.getMailboxById(mailboxId, true))
    if (entry == null) return
    await saveFavoriteEntries(addFavoriteEntry(self.getFavoriteEntryList(), entry))
  }

  self.removeFavoriteMailboxId = async (mailboxId: string): Promise<void> => {
    const entry = self.getFavoriteEntryForMailbox(self.getMailboxById(mailboxId, true))
    if (entry == null) return
    await saveFavoriteEntries(removeFavoriteEntry(self.getFavoriteEntryList(), entry))
  }

  // 웹에서 메일함 이름 변경·이동이 끝난 뒤 호출한다(JMAPMailbox.rename / changeParent).
  // 자신과 하위 폴더 경로 항목을 새 경로로 다시 쓴다. 미커스터마이즈(undefined)면 기본값이 role 기반이라 불필요.
  // 다른 기기에서 바꾼 이름은 웹이 다시 쓰지 않는다(해석되지 않는 항목으로 숨겨짐 — 허용된 한계).
  self.onFavoriteMailboxPathChanged = (oldPath: string | null, newPath: string | null): void => {
    if (oldPath == null || newPath == null) return
    const stored = getStoredFavoriteEntries()
    if (stored == null) return
    const { entries, changed } = renameFavoriteEntries(stored, oldPath, newPath)
    if (!changed) return
    saveFavoriteEntries(entries).catch((err: Error) => {
      console.error('Failed to update favorite mailboxes after rename:', err)
    })
  }

  const getMailboxExpandedKeyData = (
    idList: string[],
    isInitialized: boolean = true,
  ): MailboxExpandedKeyData => {
    return {
      isInitialized,
      idList,
      version: 1,
    }
  }

  self.getMailboxExpandedKeys = async (): Promise<string[]> => {
    if (self.isFirstLoadCalled === true) {
      let res: MailboxExpandedKeyData | null = await infoDB.getItem(
        self.infoDBKey.mailboxExpandedKeys,
      )

      if (res != null && res.isInitialized === true) {
        // version이 최신이 아니면 초기화.
        if (res.version == null || res.version < 1) {
          res = null
        }
      }

      if (res == null || res.isInitialized !== true) {
        res = getMailboxExpandedKeyData([])
      }

      return res.idList
    }

    return []
  }

  self.setMailboxExpandedKeys = (mailboxIds: string[]): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      try {
        const newData = getMailboxExpandedKeyData(mailboxIds)

        infoDB.setItem(self.infoDBKey.mailboxExpandedKeys, newData).then(() => {
          resolve(mailboxIds)
        })
      } catch (e) {
        console.log(' error addMailboxExpandedKey : ', e)
        reject(e)
      }
    })
  }

  self.signatureAPI = null as SignatureAPI | null

  self.setSignatureAPI = (signatureAPI: SignatureAPI): void => {
    self.signatureAPI = signatureAPI
  }

  self.getSignatureList = (): any[] => {
    if (self.signatureAPI != null) {
      return self.signatureAPI.getList()
    }

    return []
  }

  self.tempEmailMap = {} as { [key: string]: EmailInfo }
  self.tempEmailListener = new ListenerObj('tempEmail')

  const tempEmailQueue = new UIQueue({
    name: 'tempEmailQueue',
    size: 1,
    interval: 200,
    skipAnimationFrame: true,
  })

  const addTempEmailQueue = (type: string): void => {
    if (tempEmailQueue.queue.length > 0) {
      const item = tempEmailQueue.queue[0]
      if (item.types.indexOf(type) < 0) {
        item.types.push(type)
      }
    } else {
      const data = {
        types: [type],
        run: () => {
          self.tempEmailListener.notify({
            types: data.types,
          })
        },
      }
      tempEmailQueue.push(data)
    }
  }

  self.putTempEmail = (email: string, emailInfo: EmailInfo): void => {
    if (self.tempEmailMap[email] == null) {
      self.tempEmailMap[email] = emailInfo
      tempEmailDB.putData(email, emailInfo).then(() => {
        addTempEmailQueue('add')
      })
    }
  }

  self.addTempEmail = (emailData: string | EmailInfo | null | undefined): void => {
    // console.log(' addTempEmail : ', emailData)
    if (emailData == null) {
      return
    }
    if (typeof emailData === 'string') {
      if (isEmail(emailData) === true) {
        self.putTempEmail(emailData, { email: emailData })
      }
      return
    }
    const email = (emailData as EmailInfo).email
    if (typeof email === 'string' && isEmail(email) === true) {
      self.putTempEmail(email, emailData as EmailInfo)
    }
  }

  self.deleteTempEmail = async (email: string): Promise<any> => {
    delete self.tempEmailMap[email]
    await self.removeRecentSendEmail(email)
    const data = await tempEmailDB.deleteData(email)
    addTempEmailQueue('delete')
    return data
  }

  self.getTempEmailList = async (): Promise<{
    dataMap: Record<string, any>
    dataList: any[]
  }> => {
    const result = await tempEmailDB.getAllData()
    return result ?? { dataMap: {}, dataList: [] }
  }

  self.clearTempEmailList = (): Promise<any> => {
    return tempEmailDB.clear().then((data) => {
      addTempEmailQueue('clear')
      return data
    })
  }

  self.allowedEmailIDMap = {} as { [key: string]: boolean }

  self.addTempAllowedEmailId = (emailId: string): void => {
    self.allowedEmailIDMap[emailId] = true
  }

  self.isTempAllowedEmailId = (emailId: string): boolean => {
    return self.allowedEmailIDMap[emailId] === true
  }

  self.reloadOrgChartData = async (): Promise<any> => {
    const orgChartData = await orgChartHelper.startLoading()
    self.orgChartData = orgChartData
    return orgChartData
  }

  self.getAvatarURL = async (name: string, email: string): Promise<string | null> => {
    const url = await getAvatarURL(name, email)
    return url
  }

  self.addressMap = {} as { [key: string]: AddressObj }

  self.getAddressObj = (email: string, name?: string, nameFix: boolean = true): AddressObj => {
    const fixStr = nameFix === true ? 'F' : 'N'
    const key = name == null ? `${email}_${fixStr}` : `${email}_${fixStr}_${name}`
    if (self.addressMap[key] == null) {
      self.addressMap[key] = new AddressObj(email, name, nameFix)
    }
    return self.addressMap[key]
  }

  self.clear = (): void => {
    // 로그아웃 시 모든 데이터를 삭제해야 하면 여기서 처리.
    // infoDB.clear();
    // allowedSenderDB.clear();
    // store.remove('app.skipDraftList');

    // allowedSenderDB.clear();

    if (self.signatureAPI != null) {
      self.signatureAPI.close()
      self.signatureAPI = null
    }

    emailDB.clear()
    outboxDB.clear()
    attachmentDB.clear()
    tempEmailDB.clear()
    orgChartDB.clear()
    sharedContactDB.clear()
    jmapDownloadCache.clear()

    self.closeEvtSrc()
    window.removeEventListener('online', self.startEvtSrc)
    window.removeEventListener('offline', self.closeEvtSrc)
    window.removeEventListener('beforeunload', self.closeEvtSrc)
    window.removeEventListener('pagehide', self.closeEvtSrc)

    self.deletePrivateDataFromInfoDB()

    removeUpdateTokenListener(self.onUpdateToken)
  }

  // self.openSendMail = null;

  // self.setOpenSendMail = (func) => {
  //   self.openSendMail = func;
  // }

  // currentAccount 에서 호출되도록 변경됨.
  // self.init()

  // knownSenderCache 무효화 훅 등록.
  // 연락처 / 조직도 변경 이벤트 발생 시 캐시를 즉시 clear한다.
  // DataManager는 싱글톤이며 init()은 인스턴스를 파괴하지 않고 상태만 리셋하므로
  // listener는 앱 수명 동안 유지된다 (재등록 플래그 불필요).
  contactHelper.addContactChangeListener(clearKnownSenderCache)
  orgChartHelper.changeListener.add(clearKnownSenderCache)
}

const dataManager = new (DataManager as any)()

export default dataManager
